<script setup lang="ts">
import type { Room } from '~/shared/types/payload-types'
import { SingleRoomQuery } from '~/queries/single/room'

const route = useRoute()
const { locale } = useI18n()

const runtimeConfig = useRuntimeConfig()
const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const { data, error } = await useAsyncData(
  () => `room-${route.params.slug}-${locale.value}`,
  () =>
    $fetch('/api/payload-gql', {
      method: 'POST',
      body: {
        query: SingleRoomQuery,
        variables: {
          slug: { equals: route.params.slug },
          locale: locale.value,
          isPreview: isPreview.value,
          // with `draft: true` payload returns each doc's latest version, whatever its
          // status -- so preview must accept both, or a doc with no pending draft
          // (latest version published) matches nothing and the page renders empty
          statuses: isPreview.value ? ['draft', 'published'] : ['published'],
        },
        isPreview: isPreview.value,
      },
    }),
  { transform: (data: any) => transformPayloadData<Room>(data) }
)

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching room' })
}

if (!data.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Room not found' })
}

const room = computed(() => data.value as Room)
const floor = computed(() => room.value?.floor ?? null)

usePageSeo({
  title: () => room.value?.title,
  description: () => room.value?.description,
})

// same cache as /rooms, preloaded on idle by plugins/preload-rooms.client.ts
const { rooms: allRooms, load: loadRooms } = useRoomsData({ isPreview })
const { load: loadFloorSvg } = useFloorSvgs()

onMounted(() => {
  loadRooms({ force: isPreview.value })
  if (floor.value) loadFloorSvg(floor.value)
})

const floorRooms = computed(() =>
  allRooms.value.filter((r) => (r as any).floor === floor.value)
)
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="data?.title"
        :route="{
          title: data?.title,
        }"
        :parent="{
          name: 'rooms',
        }"
        :include-breadcrumbs="true"
      />
    </template>

    <template #default>
      <pages-rooms-slug
        v-if="data"
        :room="room"
        :floor="floor"
        :floor-rooms="floorRooms"
      />
    </template>
  </page-layout>
</template>
